import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowUpRight, CheckCircle2, Play, RefreshCw, Ticket, UserPlus, Wrench } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AppShell, DataTable, PageContainer, PageHeader, SectionContainer, StatCard, StatusBadge, UIButton } from '../../features/ui-system';
import { commandSidebarItems } from '../../features/ui-foundation/commandMenu';
import { useOperationalDataset } from './hooks/useOperationalDataset';
import { clearStaffSession, isJwtExpired, resolveBackendBaseUrl } from '../../utils/runtimeConfig';
import { appendTicketUpdate, createTicket, patchTicket } from './utils/ticketingApi';
import { deriveCustomerImpactSummary } from './utils/customerImpact';
import { deriveSiteImpactSummary } from './utils/siteImpact';
import CustomerImpactDrawer from './components/CustomerImpactDrawer';
import { createExecutionLog, fetchExecutionLogs, getExecutionActionLabel } from './utils/executionLogApi';
import {
  RUNBOOK_PRESETS,
  TICKET_SHORTCUTS,
  buildRunbookMessage,
  buildTicketShortcutNote,
  shouldMirrorRunbookToTicket,
  shouldSkipRunbookDuplicate,
} from './utils/operationalRunbook';

const API = `${resolveBackendBaseUrl()}/api`;

const statusLabel = {
  open: 'Open',
  investigating: 'Investigating',
  mitigated: 'Mitigated',
  closed: 'Closed',
};

const severityToTone = (severity) => {
  if (severity === 'critical') return 'danger';
  if (severity === 'high') return 'warn';
  return 'info';
};

const relationTypePriority = (relationType) => {
  const token = String(relationType || '').trim().toLowerCase();
  if (token === 'primary') return 0;
  if (token === 'backup') return 1;
  if (token === 'balancing') return 2;
  return 3;
};

const relationStatusTone = (status) => {
  const token = String(status || '').trim().toLowerCase();
  if (token === 'active') return 'ok';
  if (token === 'degraded') return 'warn';
  return 'neutral';
};

const relationTypeTone = (relationType) => {
  const token = String(relationType || '').trim().toLowerCase();
  if (token === 'backup') return 'warn';
  if (token === 'balancing') return 'info';
  return 'ok';
};

const ticketStatusTone = (status) => {
  const token = String(status || '').trim().toLowerCase();
  if (token === 'closed') return 'ok';
  if (token === 'mitigated') return 'info';
  if (token === 'investigating') return 'warn';
  return 'neutral';
};

const ticketPriorityTone = (priority) => {
  const token = String(priority || '').trim().toLowerCase();
  if (token === 'critical') return 'danger';
  if (token === 'high') return 'warn';
  if (token === 'medium') return 'info';
  return 'neutral';
};

const executionActionTone = (actionType) => {
  const token = String(actionType || '').trim().toLowerCase();
  if (token === 'mitigation_completed') return 'ok';
  if (token === 'mitigation_started' || token === 'investigation_started' || token === 'backup_verified') return 'warn';
  if (token === 'ticket_created' || token === 'ticket_updated') return 'info';
  return 'neutral';
};

const formatTicketStatus = (status) => {
  const token = String(status || '').trim().toLowerCase();
  if (token === 'investigating') return 'investigating';
  if (token === 'mitigated') return 'mitigated';
  if (token === 'closed') return 'closed';
  return 'open';
};

const summaryFromIncidents = (incidents, tickets) => ({
  detected: incidents.length,
  queued: incidents.filter((item) => item.status === 'open').length,
  inProgress: incidents.filter((item) => item.status === 'investigating').length,
  recovered: incidents.filter((item) => ['mitigated', 'closed'].includes(item.status)).length,
  escalated: tickets.filter((item) => item.priority === 'critical' && item.status !== 'closed').length,
});

export default function AdminRecoveryControlCenter() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, refresh, incidents, tickets, timelineEvents, lastUpdated, customers, odps, olts, nasRouters, sites } = useOperationalDataset(navigate);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [incidentState, setIncidentState] = useState([]);
  const [ticketState, setTicketState] = useState([]);
  const [manualEvents, setManualEvents] = useState([]);
  const [pathRelations, setPathRelations] = useState([]);
  const [pathLoading, setPathLoading] = useState(false);
  const [pathError, setPathError] = useState('');
  const [ticketDraft, setTicketDraft] = useState({
    title: '',
    description: '',
    status: 'open',
    priority: 'high',
    assigned_to: '',
    initial_note: '',
  });
  const [ticketUpdateNote, setTicketUpdateNote] = useState('');
  const [ticketSaving, setTicketSaving] = useState(false);
  const [showAllRelatedTickets, setShowAllRelatedTickets] = useState(false);
  const [showAllTicketLogs, setShowAllTicketLogs] = useState(false);
  const [showImpactDrawer, setShowImpactDrawer] = useState(false);
  const [executionLogs, setExecutionLogs] = useState([]);
  const [executionLoading, setExecutionLoading] = useState(false);
  const [executionError, setExecutionError] = useState('');
  const [showAllExecutionLogs, setShowAllExecutionLogs] = useState(false);
  const [executionNote, setExecutionNote] = useState('');
  const [executionSaving, setExecutionSaving] = useState(false);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('ktc_token');
    if (!token || isJwtExpired(token)) {
      clearStaffSession();
      navigate('/dadu/login');
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  useEffect(() => {
    setIncidentState(incidents);
    setTicketState(tickets);
    const params = new URLSearchParams(location.search || '');
    const requestedIncidentId = String(params.get('incidentId') || '').trim();
    const sourceType = String(params.get('sourceType') || '').trim().toLowerCase();
    const sourceId = String(params.get('sourceId') || '').trim();
    const customerId = String(params.get('customerId') || '').trim();
    const nodeType = String(params.get('nodeType') || '').trim().toLowerCase();
    const nodeId = String(params.get('nodeId') || '').trim();

    let preferredIncident = incidents.find((item) => String(item.id) === requestedIncidentId);
    if (!preferredIncident && sourceType && sourceId) {
      preferredIncident = incidents.find((item) => item.impactedEntities.includes(`${sourceType}:${sourceId}`));
    }
    if (!preferredIncident && customerId) {
      preferredIncident = incidents.find((item) => item.impactedEntities.includes(`customer:${customerId}`));
    }
    if (!preferredIncident && nodeType && nodeId) {
      preferredIncident = incidents.find((item) => item.impactedEntities.includes(`${nodeType}:${nodeId}`));
    }

    setSelectedId(preferredIncident?.id || incidents[0]?.id || '');
    setManualEvents([]);
  }, [incidents, tickets, location.search]);

  const filteredIncidents = useMemo(() => {
    const keyword = String(query || '').trim().toLowerCase();
    if (!keyword) return incidentState;
    return incidentState.filter((item) => {
      const hay = `${item.id} ${item.title} ${item.summary} ${item.suspectedCause}`.toLowerCase();
      return hay.includes(keyword);
    });
  }, [incidentState, query]);

  const selectedIncident = useMemo(
    () => filteredIncidents.find((item) => item.id === selectedId) || filteredIncidents[0] || null,
    [filteredIncidents, selectedId],
  );

  const selectedCustomerId = useMemo(() => {
    if (!selectedIncident) return '';
    const customerRef = selectedIncident.impactedEntities.find((item) => String(item).startsWith('customer:'));
    return customerRef ? String(customerRef).split(':')[1] : '';
  }, [selectedIncident]);

  const selectedInfraRef = useMemo(() => {
    if (!selectedIncident) return null;
    const infraRef = selectedIncident.impactedEntities.find((item) => (
      String(item).startsWith('odp:') || String(item).startsWith('olt:') || String(item).startsWith('nas:')
    ));
    if (!infraRef) return null;
    const [type, id] = String(infraRef).split(':');
    return { type, id };
  }, [selectedIncident]);

  const selectedRouterIds = useMemo(() => {
    if (!selectedIncident) return [];
    return selectedIncident.impactedEntities
      .filter((item) => String(item || '').startsWith('nas:'))
      .map((item) => String(item).split(':')[1])
      .filter(Boolean);
  }, [selectedIncident]);

  const executionLogContext = useMemo(() => {
    if (!selectedIncident) return null;
    const firstRouterId = selectedRouterIds[0] || '';
    const siteToken = String(selectedIncident.site_id || selectedIncident.site || '').trim();
    const siteId = siteToken && siteToken !== '-' ? siteToken : '';
    return {
      sourceType: 'incident',
      sourceId: String(selectedIncident.id || ''),
      incidentId: String(selectedIncident.id || ''),
      customerId: selectedCustomerId || '',
      routerId: firstRouterId,
      nodeId: firstRouterId,
      siteId,
    };
  }, [selectedIncident, selectedRouterIds, selectedCustomerId]);

  const relatedTickets = useMemo(() => {
    if (!selectedIncident) return [];
    const infraRefs = selectedIncident.impactedEntities.filter((item) => String(item).startsWith('nas:'));
    const incidentId = String(selectedIncident.id || '');
    const customerRef = selectedIncident.impactedEntities.find((item) => String(item).startsWith('customer:'));
    const customerId = customerRef ? String(customerRef).split(':')[1] : '';
    return ticketState.filter((ticket) => {
      const sourceType = String(ticket.sourceType || ticket.source_type || '').toLowerCase();
      const sourceId = String(ticket.sourceId || ticket.source_id || '');
      const ticketIncidentId = String(ticket.incidentId || ticket.incident_id || '');
      const ticketCustomerId = String(ticket.customerId || ticket.customer_id || '');
      const ticketRouterId = String(ticket.routerId || ticket.router_id || ticket.nodeId || ticket.node_id || '');
      if (ticketIncidentId && ticketIncidentId === incidentId) return true;
      if (sourceType === 'incident' && sourceId === incidentId) return true;
      if (customerId && ticketCustomerId === customerId) return true;
      if (ticketRouterId && infraRefs.some((ref) => String(ref).split(':')[1] === ticketRouterId)) return true;
      return false;
    });
  }, [selectedIncident, ticketState]);

  const activeRelatedTickets = useMemo(
    () => relatedTickets.filter((ticket) => String(ticket.status || '').toLowerCase() !== 'closed'),
    [relatedTickets],
  );

  const latestRelatedTicket = useMemo(
    () => relatedTickets[0] || null,
    [relatedTickets],
  );

  const selectedTicket = useMemo(() => relatedTickets[0] || null, [relatedTickets]);

  const topologyContextLink = useMemo(() => {
    const params = new URLSearchParams();
    params.set('mode', 'live');
    if (selectedRouterIds[0]) {
      params.set('sourceType', 'nas');
      params.set('sourceId', String(selectedRouterIds[0]));
      params.set('routerId', String(selectedRouterIds[0]));
      params.set('openDetail', '1');
    } else if (selectedIncident?.id) {
      params.set('sourceType', 'incident');
      params.set('sourceId', String(selectedIncident.id));
    }
    return `/topology?${params.toString()}`;
  }, [selectedIncident, selectedRouterIds]);

  useEffect(() => {
    let cancelled = false;
    const loadPathContext = async () => {
      if (!selectedIncident) {
        setPathRelations([]);
        setPathError('');
        setPathLoading(false);
        return;
      }
      if (selectedRouterIds.length === 0) {
        setPathRelations([]);
        setPathError('');
        setPathLoading(false);
        return;
      }
      const headers = getHeaders();
      if (!headers) return;
      setPathLoading(true);
      setPathError('');
      try {
        const responses = await Promise.all(
          selectedRouterIds.map(async (routerId) => {
            const response = await fetch(`${API}/routers/${encodeURIComponent(routerId)}/relations`, { headers });
            if (!response.ok) {
              const body = await response.json().catch(() => ({}));
              throw new Error(String(body?.detail || 'Gagal memuat context path.'));
            }
            const payload = await response.json();
            return Array.isArray(payload) ? payload : [];
          }),
        );
        if (cancelled) return;
        const merged = responses.flat();
        const deduped = [];
        const seen = new Set();
        merged.forEach((item) => {
          const id = String(item?.id || '').trim();
          if (!id || seen.has(id)) return;
          seen.add(id);
          deduped.push(item);
        });
        deduped.sort((a, b) => {
          const activeRank = (b?.is_active ? 1 : 0) - (a?.is_active ? 1 : 0);
          if (activeRank !== 0) return activeRank;
          const typeRank = relationTypePriority(a?.relation_type) - relationTypePriority(b?.relation_type);
          if (typeRank !== 0) return typeRank;
          return Number(a?.priority_order ?? 999) - Number(b?.priority_order ?? 999);
        });
        setPathRelations(deduped);
      } catch (error) {
        if (cancelled) return;
        setPathRelations([]);
        setPathError(String(error?.message || 'Gagal memuat konteks path.'));
      } finally {
        if (!cancelled) setPathLoading(false);
      }
    };
    loadPathContext();
    return () => {
      cancelled = true;
    };
  }, [selectedIncident, selectedRouterIds, getHeaders]);

  useEffect(() => {
    let cancelled = false;
    const loadExecutionLogs = async () => {
      if (!executionLogContext) {
        setExecutionLogs([]);
        setExecutionError('');
        setExecutionLoading(false);
        setShowAllExecutionLogs(false);
        return;
      }
      setExecutionLoading(true);
      setExecutionError('');
      try {
        const payload = await fetchExecutionLogs({
          apiBase: API,
          navigate,
          params: {
            source_type: executionLogContext.sourceType,
            source_id: executionLogContext.sourceId,
            incident_id: executionLogContext.incidentId,
            limit: 120,
          },
        });
        if (cancelled) return;
        setExecutionLogs(payload.items || []);
      } catch (error) {
        if (cancelled) return;
        setExecutionLogs([]);
        setExecutionError(String(error?.message || 'Gagal memuat execution log.'));
      } finally {
        if (!cancelled) setExecutionLoading(false);
      }
    };
    loadExecutionLogs();
    return () => {
      cancelled = true;
    };
  }, [executionLogContext, navigate]);

  const selectedEvents = useMemo(() => {
    if (!selectedIncident) return [];
    const autoEvents = timelineEvents.filter((item) => item.incidentId === selectedIncident.id);
    const opsEvents = manualEvents.filter((item) => item.incidentId === selectedIncident.id);
    return [...opsEvents, ...autoEvents];
  }, [selectedIncident, timelineEvents, manualEvents]);

  const pathViewState = useMemo(() => {
    if (!selectedIncident) return 'hidden';
    if (selectedRouterIds.length === 0) return 'not_applicable';
    if (pathLoading) return 'loading';
    if (pathError) return 'error';
    if (!pathRelations.length) return 'empty';
    return 'success';
  }, [selectedIncident, selectedRouterIds.length, pathLoading, pathError, pathRelations.length]);

  const customerImpactSummary = useMemo(() => deriveCustomerImpactSummary({
    context: {
      type: selectedIncident ? 'incident' : '',
      id: selectedIncident?.id || '',
      incident: selectedIncident || null,
      customerId: selectedCustomerId || '',
      routerId: selectedRouterIds[0] || '',
      siteId: String(selectedIncident?.site_id || selectedIncident?.site || '').trim() || '',
    },
    data: {
      customers,
      odps,
      olts,
      nasRouters,
      sites,
      incidents: incidentState,
    },
    relations: pathRelations,
  }), [selectedIncident, selectedCustomerId, selectedRouterIds, customers, odps, olts, nasRouters, sites, incidentState, pathRelations]);

  const siteImpactSummary = useMemo(() => deriveSiteImpactSummary({
    context: {
      type: selectedIncident ? 'incident' : '',
      id: selectedIncident?.id || '',
      incident: selectedIncident || null,
      incidentId: selectedIncident?.id || '',
      customerId: selectedCustomerId || '',
      routerId: selectedRouterIds[0] || '',
      siteId: String(selectedIncident?.site_id || selectedIncident?.site || '').trim() || '',
    },
    data: {
      customers,
      incidents: incidentState,
      tickets: ticketState,
      odps,
      olts,
      nasRouters,
      sites,
    },
    relations: pathRelations,
    executionLogs,
    limit: 5,
  }), [selectedIncident, selectedCustomerId, selectedRouterIds, customers, incidentState, ticketState, odps, olts, nasRouters, sites, pathRelations, executionLogs]);

  const suggestedPathActions = useMemo(() => {
    if (pathViewState !== 'success') {
      if (pathViewState === 'not_applicable') return ['Lanjutkan investigasi node tanpa konteks path.'];
      if (pathViewState === 'empty') return ['Lanjutkan investigasi node tanpa konteks path.'];
      if (pathViewState === 'error') return ['Konfirmasi status jalur secara manual.'];
      return [];
    }
    const recommendations = [];
    const relations = pathRelations;
    const primary = relations.find((item) => String(item?.relation_type || '').toLowerCase() === 'primary');
    const backup = relations.find((item) => String(item?.relation_type || '').toLowerCase() === 'backup');
    const balancing = relations.find((item) => String(item?.relation_type || '').toLowerCase() === 'balancing');
    const primaryStatus = String(primary?.link_status || '').toLowerCase();
    const backupStatus = String(backup?.link_status || '').toLowerCase();
    const balancingStatus = String(balancing?.link_status || '').toLowerCase();
    const balancingUsage = String(balancing?.path_usage || '').toLowerCase();

    const primaryIssue = Boolean(primary) && ['degraded', 'idle', 'unknown'].includes(primaryStatus);
    const backupHealthy = Boolean(backup) && backupStatus === 'active';
    const backupPresent = Boolean(backup);
    const balancingActive = Boolean(balancing) && (
      balancingStatus === 'active' || balancingUsage === 'balancing_active'
    );
    const allUnknown = relations.length > 0 && relations.every((item) => {
      const status = String(item?.link_status || 'unknown').toLowerCase();
      const usage = String(item?.path_usage || 'unknown').toLowerCase();
      return status === 'unknown' || usage === 'unknown';
    });

    if (primaryIssue) {
      if (backupHealthy) {
        recommendations.push('Verifikasi perpindahan ke jalur backup.');
      } else if (backupPresent) {
        recommendations.push('Cek kesiapan jalur cadangan.');
      } else {
        recommendations.push('Lanjutkan investigasi node tanpa konteks path.');
      }
    }

    if (balancingActive) {
      recommendations.push('Periksa distribusi trafik balancing.');
    } else if (balancing) {
      recommendations.push('Cek kesiapan jalur balancing.');
    }

    if (allUnknown) {
      recommendations.push('Konfirmasi status jalur secara manual.');
    }

    if (customerImpactSummary.state === 'ok') {
      if (customerImpactSummary.severity === 'critical' && customerImpactSummary.mitigation.backupAvailable) {
        recommendations.unshift('Dampak pelanggan tinggi. Verifikasi perpindahan ke jalur backup sekarang.');
      } else if (customerImpactSummary.severity === 'critical') {
        recommendations.unshift('Dampak pelanggan tinggi. Prioritaskan isolasi gangguan pada node utama.');
      } else if (customerImpactSummary.severity === 'high' && customerImpactSummary.mitigation.balancingAvailable) {
        recommendations.unshift('Dampak pelanggan menengah-tinggi. Aktifkan mitigasi balancing jika memungkinkan.');
      }
    }

    if (!recommendations.length) {
      recommendations.push('Lanjutkan investigasi node tanpa konteks path.');
    }
    return [...new Set(recommendations)].slice(0, 3);
  }, [pathViewState, pathRelations, customerImpactSummary]);

  const summary = useMemo(
    () => summaryFromIncidents(incidentState, ticketState),
    [incidentState, ticketState],
  );

  const appendManualEvent = (incidentId, title, detail, tone = 'info') => {
    setManualEvents((prev) => [
      {
        id: `MAN-${prev.length + 1}`,
        incidentId,
        timestamp: new Date().toISOString(),
        title,
        detail,
        tone,
        source: 'Operator',
      },
      ...prev,
    ]);
  };

  const emitExecutionLog = useCallback(async (actionType, message, context = executionLogContext) => {
    if (!context || !message) return null;
    try {
      const created = await createExecutionLog({
        apiBase: API,
        navigate,
        payload: {
          source_type: context.sourceType,
          source_id: context.sourceId,
          incident_id: context.incidentId || undefined,
          customer_id: context.customerId || undefined,
          router_id: context.routerId || undefined,
          node_id: context.nodeId || undefined,
          site_id: context.siteId || undefined,
          action_type: actionType,
          message,
        },
      });
      if (created) {
        setExecutionLogs((prev) => [created, ...prev.filter((item) => String(item.id) !== String(created.id))]);
      }
      return created;
    } catch (_error) {
      return null;
    }
  }, [executionLogContext, navigate]);

  const handleManualExecutionNote = useCallback(async () => {
    const note = String(executionNote || '').trim();
    if (!note) {
      toast.error('Catatan execution log belum diisi.');
      return;
    }
    if (!executionLogContext) return;
    setExecutionSaving(true);
    try {
      const created = await createExecutionLog({
        apiBase: API,
        navigate,
        payload: {
          source_type: executionLogContext.sourceType,
          source_id: executionLogContext.sourceId,
          incident_id: executionLogContext.incidentId || undefined,
          customer_id: executionLogContext.customerId || undefined,
          router_id: executionLogContext.routerId || undefined,
          node_id: executionLogContext.nodeId || undefined,
          site_id: executionLogContext.siteId || undefined,
          action_type: 'manual_note',
          message: note,
        },
      });
      if (created) {
        setExecutionLogs((prev) => [created, ...prev.filter((item) => String(item.id) !== String(created.id))]);
      }
      setExecutionNote('');
      toast.success('Execution log tersimpan.');
    } catch (error) {
      toast.error(String(error?.message || 'Gagal menyimpan execution log.'));
    } finally {
      setExecutionSaving(false);
    }
  }, [executionNote, executionLogContext, navigate]);

  const visibleExecutionLogs = useMemo(() => {
    const items = Array.isArray(executionLogs) ? executionLogs : [];
    return showAllExecutionLogs ? items : items.slice(0, 5);
  }, [executionLogs, showAllExecutionLogs]);
  const latestExecutionLog = useMemo(
    () => (Array.isArray(executionLogs) && executionLogs.length > 0 ? executionLogs[0] : null),
    [executionLogs],
  );

  const handleRunbookPreset = useCallback(async (preset) => {
    if (!selectedIncident || !executionLogContext || !preset) return;
    const contextLabel = selectedIncident.id || selectedIncident.title || '';
    const message = buildRunbookMessage({ presetId: preset.id, contextLabel });
    if (shouldSkipRunbookDuplicate({ logs: executionLogs, actionType: preset.actionType || 'manual_note', message })) {
      toast.info('Preset sama baru saja dicatat. Menghindari log duplikat.');
      return;
    }
    setExecutionSaving(true);
    try {
      const created = await emitExecutionLog(preset.actionType || 'manual_note', message);
      if (created) {
        setExecutionLogs((prev) => [created, ...prev.filter((item) => String(item.id) !== String(created.id))]);
      }
      if (selectedTicket?.id && shouldMirrorRunbookToTicket({ presetId: preset.id, actionType: preset.actionType })) {
        const headers = getHeaders();
        if (headers) {
          const updated = await appendTicketUpdate({
            headers,
            ticketId: selectedTicket.id,
            note: buildTicketShortcutNote(preset.label, contextLabel),
          });
          setTicketState((prev) => prev.map((item) => (String(item.id) === String(updated.id) ? updated : item)));
        }
      }
      toast.success(`Preset "${preset.label}" tercatat.`);
    } catch (error) {
      toast.error(String(error?.message || 'Gagal menjalankan preset runbook.'));
    } finally {
      setExecutionSaving(false);
    }
  }, [emitExecutionLog, executionLogContext, getHeaders, selectedIncident, selectedTicket, executionLogs]);

  const handleTicketShortcut = useCallback(async (shortcut) => {
    if (!selectedTicket?.id || !shortcut) return;
    const headers = getHeaders();
    if (!headers) return;
    setTicketSaving(true);
    try {
      const nextStatus = String(shortcut.status || '').toLowerCase();
      const updated = await patchTicket({
        headers,
        ticketId: selectedTicket.id,
        payload: {
          status: nextStatus,
          assigned_to: selectedTicket.assignedTo || selectedTicket.assigned_to || undefined,
          priority: selectedTicket.priority || 'medium',
        },
      });
      const shortcutNote = buildTicketShortcutNote(shortcut.label, selectedIncident?.id || selectedTicket.ticketCode || selectedTicket.id);
      const updatedWithLog = await appendTicketUpdate({ headers, ticketId: selectedTicket.id, note: shortcutNote });
      setTicketState((prev) => prev.map((item) => {
        if (String(item.id) !== String(selectedTicket.id)) return item;
        return { ...item, ...updated, ...updatedWithLog };
      }));
      await emitExecutionLog(shortcut.actionType || 'ticket_updated', shortcutNote);
      toast.success(`Shortcut "${shortcut.label}" dijalankan.`);
    } catch (error) {
      toast.error(String(error?.message || 'Gagal menjalankan shortcut ticket.'));
    } finally {
      setTicketSaving(false);
    }
  }, [emitExecutionLog, getHeaders, selectedIncident, selectedTicket]);

  const handleAction = (action) => {
    if (!selectedIncident) return;
    if (action === 'assign') {
      setTicketState((prev) => prev.map((item) => (
        item.incidentId === selectedIncident.id
          ? { ...item, assignee: 'NOC-Shift-C', status: 'in_progress' }
          : item
      )));
      appendManualEvent(selectedIncident.id, 'Assigned', 'Incident dipindah ke NOC-Shift-C.', 'info');
      void emitExecutionLog('investigation_started', `Incident ${selectedIncident.id} ditugaskan ke NOC-Shift-C.`);
      return;
    }
    if (action === 'open_ticket') {
      setTicketDraft((prev) => ({
        ...prev,
        title: prev.title || `Incident ${selectedIncident.id} - ${selectedIncident.title}`,
      }));
      return;
    }
    if (action === 'escalate') {
      setTicketState((prev) => prev.map((item) => (
        item.incidentId === selectedIncident.id
          ? { ...item, status: 'escalated', assignee: 'Field Team' }
          : item
      )));
      appendManualEvent(selectedIncident.id, 'Escalated', 'Kasus dieskalasi ke field team.', 'danger');
      void emitExecutionLog('mitigation_started', `Escalation dimulai untuk incident ${selectedIncident.id}.`);
      return;
    }
    if (action === 'mark_mitigated') {
      setIncidentState((prev) => prev.map((item) => (
        item.id === selectedIncident.id ? { ...item, status: 'mitigated' } : item
      )));
      appendManualEvent(selectedIncident.id, 'Marked Mitigated', 'Layanan utama pulih, menunggu verifikasi akhir.', 'ok');
      void emitExecutionLog('mitigation_completed', `Mitigasi incident ${selectedIncident.id} ditandai selesai.`);
      return;
    }
    setIncidentState((prev) => prev.map((item) => (
      item.id === selectedIncident.id ? { ...item, status: 'investigating' } : item
    )));
    appendManualEvent(selectedIncident.id, 'Run Recovery', 'Playbook recovery dijalankan.', 'warn');
    void emitExecutionLog('recovery_opened', `Recovery runbook dijalankan untuk incident ${selectedIncident.id}.`);
  };

  const handleCreateTicket = async () => {
    if (!selectedIncident) return;
    const headers = getHeaders();
    if (!headers) return;
    if (!String(ticketDraft.title || '').trim()) {
      toast.error('Judul ticket wajib diisi.');
      return;
    }
    setTicketSaving(true);
    try {
      const firstRouterId = selectedRouterIds[0] || '';
      const created = await createTicket({
        headers,
        payload: {
          title: ticketDraft.title,
          description: ticketDraft.description,
          status: ticketDraft.status,
          priority: ticketDraft.priority,
          assigned_to: ticketDraft.assigned_to || undefined,
          initial_note: ticketDraft.initial_note || undefined,
          source_type: 'incident',
          source_id: selectedIncident.id,
          incident_id: selectedIncident.id,
          customer_id: selectedCustomerId || undefined,
          router_id: firstRouterId || undefined,
          node_id: firstRouterId || undefined,
      site_id: String(selectedIncident.site_id || selectedIncident.site || '').trim() || undefined,
        },
      });
      setTicketState((prev) => [created.item, ...prev]);
      created.warnings.forEach((warning) => toast.warning(warning));
      toast.success(`Ticket ${created.item.ticketCode || created.item.id} berhasil dibuat.`);
      appendManualEvent(selectedIncident.id, 'Ticket Created', 'Ticket operasional terhubung ke incident ini.', 'warn');
      setTicketDraft({
        title: '',
        description: '',
        status: 'open',
        priority: 'high',
        assigned_to: '',
        initial_note: '',
      });
    } catch (err) {
      toast.error(err?.message || 'Gagal membuat ticket.');
    } finally {
      setTicketSaving(false);
    }
  };

  const handleAddTicketUpdate = async (ticketId) => {
    const headers = getHeaders();
    if (!headers || !ticketId) return;
    if (!String(ticketUpdateNote || '').trim()) {
      toast.error('Catatan update belum diisi.');
      return;
    }
    setTicketSaving(true);
    try {
      const updated = await appendTicketUpdate({ headers, ticketId, note: ticketUpdateNote });
      setTicketState((prev) => prev.map((item) => (String(item.id) === String(ticketId) ? updated : item)));
      setTicketUpdateNote('');
      toast.success('Update ticket tersimpan.');
    } catch (err) {
      toast.error(err?.message || 'Gagal menambah update ticket.');
    } finally {
      setTicketSaving(false);
    }
  };

  const queueColumns = [
    { key: 'incident', label: 'Incident' },
    { key: 'severity', label: 'Severity' },
    { key: 'status', label: 'Status' },
    { key: 'site', label: 'Site' },
    { key: 'age', label: 'Age' },
  ];

  return (
    <AppShell sidebarItems={commandSidebarItems} activePathname="/recovery" activeSearch="" initialTab="overview">
      <PageContainer>
        <PageHeader
          eyebrow="Recovery & Actions"
          title="Operational Recovery Queue"
          subtitle="Prioritas tindakan operasional: pilih incident, cek dampak, lalu jalankan aksi berikutnya."
          action={(
            <UIButton variant="ghost" onClick={refresh} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </UIButton>
          )}
        />

        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
          <StatCard title="Detected" value={summary.detected} note="Total incidents" tone="neutral" />
          <StatCard title="Queued" value={summary.queued} note="Menunggu triage" tone="info" />
          <StatCard title="In Progress" value={summary.inProgress} note="Sedang ditangani" tone="warn" />
          <StatCard title="Recovered" value={summary.recovered} note="Mitigated / Closed" tone="ok" />
          <StatCard title="Escalated" value={summary.escalated} note="Butuh tindakan lapangan" tone="danger" />
        </div>

        <div className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
          <SectionContainer className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Cari incident id/title/cause..."
                className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 md:max-w-md"
              />
              {error ? <span className="text-xs text-rose-300">{error}</span> : null}
              <span className="ml-auto text-xs text-slate-500">Last update: {lastUpdated ? new Date(lastUpdated).toLocaleString('id-ID') : '-'}</span>
            </div>

            <DataTable
              title="Recovery Queue"
              subtitle="Daftar incident aktif untuk ditangani."
              columns={queueColumns}
              rows={filteredIncidents}
              loading={loading}
              emptyMessage="Tidak ada incident pada scope ini. Sistem terlihat stabil atau relasi incident belum lengkap."
              rowKey="id"
              onRowClick={(row) => setSelectedId(row.id)}
              renderCell={(row, column) => {
                if (column.key === 'incident') {
                  return (
                    <div>
                      <p className="text-sm font-semibold text-white">{row.id}</p>
                      <p className="text-xs text-slate-400">{row.title}</p>
                    </div>
                  );
                }
                if (column.key === 'severity') {
                  return <StatusBadge tone={severityToTone(row.severity)}>{String(row.severity).toUpperCase()}</StatusBadge>;
                }
                if (column.key === 'status') {
                  return <StatusBadge tone={row.status === 'mitigated' ? 'ok' : row.status === 'investigating' ? 'warn' : 'neutral'}>{statusLabel[row.status] || row.status}</StatusBadge>;
                }
                if (column.key === 'site') return row.site || '-';
                if (column.key === 'age') return new Date(row.createdAt).toLocaleTimeString('id-ID');
                return '-';
              }}
            />
          </SectionContainer>

          <SectionContainer className="space-y-3">
            {!selectedIncident ? (
              <p className="rounded-lg border border-white/10 bg-[#0a1322] px-3 py-4 text-sm text-slate-400">
                Tidak ada incident terpilih. Pilih item di queue atau kirim context `incidentId` dari halaman lain.
              </p>
            ) : (
              <>
                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-white">{selectedIncident.title}</p>
                      <p className="text-xs text-slate-400">{selectedIncident.id}</p>
                    </div>
                    <StatusBadge tone={severityToTone(selectedIncident.severity)}>
                      {String(selectedIncident.severity).toUpperCase()}
                    </StatusBadge>
                  </div>
                  <p className="text-xs text-slate-300">{selectedIncident.summary}</p>
                  <p className="mt-1 text-xs text-slate-400">Probable cause: {selectedIncident.suspectedCause || '-'}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {selectedIncident.impactedEntities.map((entity) => (
                      <span key={entity} className="rounded-md border border-white/10 bg-[#13213a] px-2 py-1 text-[11px] text-slate-200">
                        {entity}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Ticket Context</p>
                  <p className="mb-2 text-[11px] text-slate-400">
                    Context: incident <span className="text-slate-200">{selectedIncident.id}</span>
                    {selectedRouterIds[0] ? <> | router <span className="text-slate-200">{selectedRouterIds[0]}</span></> : null}
                    {selectedCustomerId ? <> | customer <span className="text-slate-200">{selectedCustomerId}</span></> : null}
                  </p>
                  {relatedTickets.length === 0 ? (
                    <p className="text-xs text-slate-400">Belum ada ticket terkait. Buat ticket untuk mulai melacak penanganan.</p>
                  ) : (
                    <div className="space-y-1.5">
                      <div className="mb-1 flex items-center justify-between gap-2 text-[11px] text-slate-400">
                        <span>Aktif: <span className="text-slate-100">{activeRelatedTickets.length}</span></span>
                        <span>Terbaru: <span className="text-slate-100">{latestRelatedTicket?.ticketCode || latestRelatedTicket?.id || '-'}</span></span>
                      </div>
                      {(showAllRelatedTickets ? relatedTickets : relatedTickets.slice(0, 3)).map((ticket) => (
                        <div key={ticket.id} className="rounded-md border border-white/10 bg-[#13213a] px-2.5 py-2">
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <p className="text-xs font-semibold text-white">{ticket.ticketCode || ticket.id}</p>
                            <StatusBadge tone={ticketStatusTone(ticket.status)}>
                              {formatTicketStatus(ticket.status)}
                            </StatusBadge>
                          </div>
                          <p className="text-[11px] text-slate-300">{ticket.title || ticket.summary || '-'}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-1.5">
                            <StatusBadge tone={ticketPriorityTone(ticket.priority)}>{String(ticket.priority || 'medium').toLowerCase()}</StatusBadge>
                            <span className="text-[10px] text-slate-500">PIC: {ticket.assignedTo || '-'}</span>
                            <span className="text-[10px] text-slate-500">Update: {ticket.updated_at ? new Date(ticket.updated_at).toLocaleString('id-ID') : '-'}</span>
                          </div>
                        </div>
                      ))}
                      {relatedTickets.length > 3 ? (
                        <button
                          type="button"
                          className="text-[11px] text-cyan-300 hover:text-cyan-200"
                          onClick={() => setShowAllRelatedTickets((prev) => !prev)}
                        >
                          {showAllRelatedTickets ? 'Tampilkan ringkas' : 'Tampilkan lebih banyak'}
                        </button>
                      ) : null}
                    </div>
                  )}
                  <div className="mt-2 grid gap-2">
                    <input
                      value={ticketDraft.title}
                      onChange={(event) => setTicketDraft((prev) => ({ ...prev, title: event.target.value }))}
                      placeholder={selectedIncident ? `Incident ${selectedIncident.id} - ${selectedIncident.title}` : 'Judul ticket'}
                      className="w-full rounded-lg border border-white/10 bg-[#101b2e] px-2.5 py-2 text-xs text-slate-100 placeholder:text-slate-500"
                    />
                    <textarea
                      value={ticketDraft.description}
                      onChange={(event) => setTicketDraft((prev) => ({ ...prev, description: event.target.value }))}
                      rows={2}
                      placeholder="Deskripsi singkat penanganan"
                      className="w-full rounded-lg border border-white/10 bg-[#101b2e] px-2.5 py-2 text-xs text-slate-100 placeholder:text-slate-500"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={ticketDraft.priority}
                        onChange={(event) => setTicketDraft((prev) => ({ ...prev, priority: event.target.value }))}
                        className="rounded-lg border border-white/10 bg-[#101b2e] px-2 py-2 text-xs text-slate-200"
                      >
                        <option value="low">low</option>
                        <option value="medium">medium</option>
                        <option value="high">high</option>
                        <option value="critical">critical</option>
                      </select>
                      <input
                        value={ticketDraft.assigned_to}
                        onChange={(event) => setTicketDraft((prev) => ({ ...prev, assigned_to: event.target.value }))}
                        placeholder="PIC / owner"
                        className="rounded-lg border border-white/10 bg-[#101b2e] px-2 py-2 text-xs text-slate-100 placeholder:text-slate-500"
                      />
                    </div>
                    <UIButton variant="subtle" onClick={handleCreateTicket} disabled={ticketSaving}>
                      <Ticket className="h-4 w-4" />
                      Buat Ticket
                    </UIButton>
                    {activeRelatedTickets.length > 0 ? (
                      <p className="text-[11px] text-amber-300">
                        Sudah ada {activeRelatedTickets.length} ticket aktif untuk source ini. Ticket terbaru: {latestRelatedTicket?.ticketCode || latestRelatedTicket?.id || '-'}.
                        Anda tetap bisa membuat ticket baru jika diperlukan.
                      </p>
                    ) : null}
                  </div>
                  {selectedTicket ? (
                    <div className="mt-2 space-y-2 rounded-md border border-white/10 bg-[#101b2e] p-2.5">
                      <p className="text-[11px] uppercase tracking-[0.1em] text-slate-400">Worklog Ticket Aktif</p>
                      <div className="flex flex-wrap gap-1.5">
                        {TICKET_SHORTCUTS.map((shortcut) => (
                          <button
                            key={shortcut.id}
                            type="button"
                            onClick={() => handleTicketShortcut(shortcut)}
                            disabled={ticketSaving}
                            className="rounded-md border border-cyan-500/30 bg-cyan-500/10 px-2 py-1 text-[11px] text-cyan-100 hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {shortcut.label}
                          </button>
                        ))}
                      </div>
                      {(showAllTicketLogs ? (selectedTicket.actionLog || []).slice().reverse() : (selectedTicket.actionLog || []).slice(-3).reverse()).map((entry) => (
                        <p key={entry.id || `${entry.created_at}-${entry.message}`} className="text-[11px] text-slate-300">
                          {entry.message} <span className="text-slate-500">({entry.actor || '-'} | {entry.created_at ? new Date(entry.created_at).toLocaleString('id-ID') : '-'})</span>
                        </p>
                      ))}
                      {(selectedTicket.actionLog || []).length > 3 ? (
                        <button
                          type="button"
                          className="text-[11px] text-cyan-300 hover:text-cyan-200"
                          onClick={() => setShowAllTicketLogs((prev) => !prev)}
                        >
                          {showAllTicketLogs ? 'Tampilkan log terbaru saja' : 'Lihat semua log'}
                        </button>
                      ) : null}
                      <div className="flex gap-2">
                        <input
                          value={ticketUpdateNote}
                          onChange={(event) => setTicketUpdateNote(event.target.value)}
                          placeholder="Tulis update investigasi atau tindakan yang dilakukan"
                          className="flex-1 rounded-lg border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500"
                        />
                        <UIButton variant="ghost" onClick={() => handleAddTicketUpdate(selectedTicket.id)} disabled={ticketSaving}>
                          Simpan Update
                        </UIButton>
                      </div>
                    </div>
                  ) : null}
                  <div className="mt-2 space-y-1 rounded-md border border-white/10 bg-[#101b2e] p-2.5">
                    <p className="text-[11px] uppercase tracking-[0.1em] text-slate-400">Runbook Preset</p>
                    <div className="flex flex-wrap gap-1.5">
                      {RUNBOOK_PRESETS.map((preset) => (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => handleRunbookPreset(preset)}
                          disabled={executionSaving}
                          className="rounded-md border border-white/15 bg-[#0f1a2b] px-2 py-1 text-[11px] text-slate-200 hover:border-cyan-400/60 hover:text-cyan-200 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-xs uppercase tracking-[0.12em] text-slate-400">Customer Impact</p>
                    <StatusBadge tone={customerImpactSummary.severityTone}>{String(customerImpactSummary.severityLabel || 'low').toUpperCase()}</StatusBadge>
                  </div>
                  {customerImpactSummary.state !== 'ok' ? (
                    <p className="text-xs text-slate-400">{customerImpactSummary.message}</p>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <StatusBadge tone="neutral">Impacted {customerImpactSummary.counts.total}</StatusBadge>
                        <StatusBadge tone={customerImpactSummary.counts.offline > 0 ? 'danger' : 'neutral'}>Offline {customerImpactSummary.counts.offline}</StatusBadge>
                        <StatusBadge tone={customerImpactSummary.counts.degraded > 0 ? 'warn' : 'neutral'}>Degraded {customerImpactSummary.counts.degraded}</StatusBadge>
                        <StatusBadge tone={customerImpactSummary.counts.warning > 0 ? 'info' : 'neutral'}>Warning {customerImpactSummary.counts.warning}</StatusBadge>
                      </div>
                      {customerImpactSummary.sites.length > 0 ? (
                        <p className="text-[11px] text-slate-400">
                          Site impacted: {customerImpactSummary.sites.slice(0, 2).map((item) => `${item.site} (${item.total})`).join(' | ')}
                        </p>
                      ) : (
                        <p className="text-[11px] text-slate-500">Site impact belum bisa dipetakan dari relasi customer saat ini.</p>
                      )}
                      {customerImpactSummary.topCustomers.length > 0 ? (
                        <div className="space-y-1.5">
                          {customerImpactSummary.topCustomers.map((customer) => (
                            <div key={customer.id} className="rounded-md border border-white/10 bg-[#13213a] px-2.5 py-2">
                              <p className="text-[11px] font-semibold text-white">{customer.name} ({customer.code})</p>
                              <p className="text-[10px] text-slate-400">
                                {String(customer.state || 'unknown').toUpperCase()} | {customer.site || '-'}
                              </p>
                            </div>
                          ))}
                          {customerImpactSummary.counts.total > customerImpactSummary.topCustomers.length ? (
                            <button
                              type="button"
                              onClick={() => setShowImpactDrawer(true)}
                              className="text-[11px] text-cyan-300 hover:text-cyan-200"
                            >
                              +{customerImpactSummary.counts.total - customerImpactSummary.topCustomers.length} lainnya | Lihat semua
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                      {customerImpactSummary.mitigation.hint ? (
                        <p className="text-[11px] text-cyan-200">{customerImpactSummary.mitigation.hint}</p>
                      ) : null}
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-xs uppercase tracking-[0.12em] text-slate-400">Site Impact</p>
                    <StatusBadge tone={siteImpactSummary.selectedSite?.severityTone || 'neutral'}>
                      {String(siteImpactSummary.selectedSite?.severity || 'low').toUpperCase()}
                    </StatusBadge>
                  </div>
                  {siteImpactSummary.state !== 'ok' || (siteImpactSummary.topSites || []).length === 0 ? (
                    <p className="text-xs text-slate-400">{siteImpactSummary.message || 'Belum ada dampak site yang terdeteksi.'}</p>
                  ) : (
                    <div className="space-y-1.5">
                      {siteImpactSummary.topSites.slice(0, 3).map((site) => (
                        <div key={site.site_id} className="rounded-md border border-white/10 bg-[#13213a] px-2.5 py-2">
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <p className="text-[11px] font-semibold text-white">{site.site_name || site.site_id}</p>
                            <StatusBadge tone={site.severityTone}>{String(site.severity || 'low').toUpperCase()}</StatusBadge>
                          </div>
                          <p className="text-[10px] text-slate-300">
                            Impacted {site.impacted_customers_total}
                            {' | '}
                            Offline {site.offline_count}
                            {' | '}
                            Degraded {site.degraded_count}
                            {' | '}
                            Incident {site.active_incident_count}
                            {' | '}
                            Ticket {site.active_ticket_count}
                          </p>
                          <p className="mt-1 text-[10px] text-slate-400">
                            Node terkait: {site.top_related_nodes?.slice(0, 2).map((node) => node.name || node.id).join(' | ') || 'Belum ada node terkait'}
                          </p>
                          <p className="mt-1 text-[10px] text-slate-400">
                            Last operational: {site.last_operational_update
                              ? `${getExecutionActionLabel(site.last_operational_update.actionType)} | ${site.last_operational_update.createdAt ? new Date(site.last_operational_update.createdAt).toLocaleString('id-ID') : '-'}`
                              : 'Belum ada tindakan tercatat'}
                          </p>
                          {site.mitigation_hint ? (
                            <p className="mt-1 text-[10px] text-cyan-200">{site.mitigation_hint}</p>
                          ) : null}
                        </div>
                      ))}
                      {siteImpactSummary.topSites.length > 3 ? (
                        <p className="text-[11px] text-slate-400">+{siteImpactSummary.topSites.length - 3} site lainnya terdampak</p>
                      ) : null}
                      <div className="flex flex-wrap gap-2">
                        <UIButton as="link" to={selectedIncident ? `/recovery?incidentId=${encodeURIComponent(selectedIncident.id)}&view=site-impact` : '/recovery'} variant="ghost">Open in Recovery</UIButton>
                        <UIButton as="link" to={siteImpactSummary.selectedSite?.site_id ? `/topology?mode=live&siteId=${encodeURIComponent(String(siteImpactSummary.selectedSite.site_id || ''))}&openDetail=1` : '/topology?mode=live'} variant="ghost">Open in Topology</UIButton>
                        <UIButton as="link" to={siteImpactSummary.selectedSite?.site_id ? `/infrastructure?tab=sites&siteId=${encodeURIComponent(String(siteImpactSummary.selectedSite.site_id || ''))}` : '/infrastructure?tab=sites'} variant="ghost">Open in Infrastructure</UIButton>
                        <UIButton as="link" to={siteImpactSummary.selectedSite?.site_id ? `/tickets?siteId=${encodeURIComponent(String(siteImpactSummary.selectedSite.site_id || ''))}` : '/tickets'} variant="ghost">Open in Tickets</UIButton>
                      </div>
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Path Context</p>
                  {pathViewState === 'not_applicable' ? (
                    <p className="text-xs text-slate-400">Path context tidak berlaku untuk node ini.</p>
                  ) : null}
                  {pathViewState === 'loading' ? (
                    <p className="text-xs text-slate-400">Masih memuat data...</p>
                  ) : null}
                  {pathViewState === 'error' ? (
                    <p className="text-xs text-rose-300">{pathError}</p>
                  ) : null}
                  {pathViewState === 'empty' ? (
                    <p className="text-xs text-slate-400">Belum ada relation path terdaftar untuk router ini.</p>
                  ) : null}
                  {pathViewState === 'success' ? (
                    <div className="space-y-1.5">
                      {pathRelations.slice(0, 6).map((relation) => {
                        const fromName = relation?.from_router_name || relation?.from_router_id || '-';
                        const toName = relation?.to_router_name || relation?.to_router_id || '-';
                        const fromRole = relation?.from_router_role || 'distribution';
                        const toRole = relation?.to_router_role || 'distribution';
                        const relationType = String(relation?.relation_type || 'primary').toLowerCase();
                        return (
                          <div key={String(relation?.id)} className="rounded-md border border-white/10 bg-[#13213a] px-2.5 py-2">
                            <p className="text-[11px] text-slate-100">
                              {fromName} ({fromRole}) -> {toName} ({toRole})
                            </p>
                            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                              <StatusBadge tone={relationTypeTone(relationType)}>{relationType}</StatusBadge>
                              <StatusBadge tone={relationStatusTone(relation?.link_status)}>{String(relation?.link_status || 'unknown').toUpperCase()}</StatusBadge>
                              {relation?.priority_order != null ? <span className="text-[10px] text-slate-400">Priority {relation.priority_order}</span> : null}
                            </div>
                            <p className="mt-1 text-[10px] text-slate-400">
                              Usage: <span className="text-slate-200">{String(relation?.path_usage || 'unknown').replaceAll('_', ' ')}</span>
                              {' | '}
                              Traffic: <span className="text-slate-200">{relation?.traffic_hint || 'Telemetry path belum tersedia'}</span>
                              {' | '}
                              Last seen: <span className="text-slate-200">{relation?.last_seen ? new Date(relation.last_seen).toLocaleString('id-ID') : '-'}</span>
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Suggested Recovery Actions</p>
                  <div className="space-y-1.5">
                    {suggestedPathActions.length === 0 ? (
                      <p className="text-xs text-slate-400">Saran investigasi path belum tersedia.</p>
                    ) : suggestedPathActions.map((action, index) => (
                      <p key={`path-action-${index}`} className="rounded-md border border-white/10 bg-[#13213a] px-2.5 py-2 text-xs text-slate-200">
                        {action}
                      </p>
                    ))}
                  </div>
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Actions</p>
                  <div className="grid grid-cols-2 gap-2">
                    <UIButton variant="subtle" onClick={() => handleAction('assign')}><UserPlus className="h-4 w-4" />Assign</UIButton>
                    <UIButton variant="ghost" onClick={() => handleAction('open_ticket')}><Ticket className="h-4 w-4" />Open in Tickets</UIButton>
                    <UIButton variant="danger" onClick={() => handleAction('escalate')}><AlertTriangle className="h-4 w-4" />Escalate</UIButton>
                    <UIButton variant="subtle" onClick={() => handleAction('mark_mitigated')}><CheckCircle2 className="h-4 w-4" />Mark Mitigated</UIButton>
                    <UIButton variant="primary" className="col-span-2" onClick={() => handleAction('run_recovery')}>
                      <Play className="h-4 w-4" />Run Recovery
                    </UIButton>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <UIButton
                      as="link"
                      to={topologyContextLink}
                      variant="ghost"
                      onClick={() => {
                        if (!selectedIncident) return;
                        void emitExecutionLog('topology_opened', `Topology dibuka dari recovery incident ${selectedIncident.id}.`);
                      }}
                    >
                      <ArrowUpRight className="h-4 w-4" />Open in Topology
                    </UIButton>
                    <UIButton
                      as="link"
                      to={selectedCustomerId ? `/customers?customerId=${encodeURIComponent(selectedCustomerId)}&incidentId=${encodeURIComponent(selectedIncident.id)}` : '/customers'}
                      variant="ghost"
                    >
                      <Wrench className="h-4 w-4" />View Customer
                    </UIButton>
                    <UIButton
                      as="link"
                      to={selectedInfraRef ? `/infrastructure?nodeType=${encodeURIComponent(selectedInfraRef.type)}&nodeId=${encodeURIComponent(selectedInfraRef.id)}&incidentId=${encodeURIComponent(selectedIncident.id)}` : '/infrastructure'}
                      variant="ghost"
                      className="col-span-2"
                    >
                      Open in Infrastructure
                    </UIButton>
                    {selectedRouterIds[0] ? (
                      <UIButton
                        as="link"
                        to={`/infrastructure?nodeType=nas&nodeId=${encodeURIComponent(selectedRouterIds[0])}&relationFocus=1`}
                        variant="ghost"
                        className="col-span-2"
                      >
                        View Router Relations
                      </UIButton>
                    ) : null}
                  </div>
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Last Operational Update</p>
                  {latestExecutionLog ? (
                    <div className="space-y-1 text-xs text-slate-300">
                      <p className="font-semibold text-white">{getExecutionActionLabel(latestExecutionLog.actionType)}</p>
                      <p>{latestExecutionLog.message || '-'}</p>
                      <p className="text-[11px] text-slate-500">
                        {latestExecutionLog.actor || '-'} | {latestExecutionLog.createdAt ? new Date(latestExecutionLog.createdAt).toLocaleString('id-ID') : '-'}
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">Belum ada tindakan tercatat.</p>
                  )}
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-xs uppercase tracking-[0.12em] text-slate-400">Execution Log</p>
                    <span className="text-[10px] text-slate-500">Total {executionLogs.length}</span>
                  </div>
                  {executionLoading ? (
                    <p className="text-xs text-slate-400">Masih memuat data...</p>
                  ) : null}
                  {!executionLoading && executionError ? (
                    <p className="text-xs text-rose-300">{executionError}</p>
                  ) : null}
                  {!executionLoading && !executionError && executionLogs.length === 0 ? (
                    <p className="text-xs text-slate-400">Belum ada data terkait. Langkah operasional yang dilakukan akan muncul di sini.</p>
                  ) : null}
                  {!executionLoading && !executionError && executionLogs.length > 0 ? (
                    <div className="space-y-1.5">
                      {visibleExecutionLogs.map((entry) => (
                        <div key={entry.id || `${entry.createdAt}-${entry.message}`} className="rounded-md border border-white/10 bg-[#13213a] px-2.5 py-2">
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <p className="text-xs font-semibold text-white">{getExecutionActionLabel(entry.actionType)}</p>
                            <StatusBadge tone={executionActionTone(entry.actionType)}>{entry.actor || '-'}</StatusBadge>
                          </div>
                          <p className="text-[11px] text-slate-300">{entry.message}</p>
                          <p className="mt-1 text-[10px] text-slate-500">{entry.createdAt ? new Date(entry.createdAt).toLocaleString('id-ID') : '-'}</p>
                        </div>
                      ))}
                      {executionLogs.length > 5 ? (
                        <button
                          type="button"
                          className="text-[11px] text-cyan-300 hover:text-cyan-200"
                          onClick={() => setShowAllExecutionLogs((prev) => !prev)}
                        >
                          {showAllExecutionLogs ? 'Tampilkan log terbaru saja' : 'Lihat semua'}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="mt-2 flex gap-2">
                    <input
                      value={executionNote}
                      onChange={(event) => setExecutionNote(event.target.value)}
                      placeholder="Tambah catatan runbook operasional"
                      className="flex-1 rounded-lg border border-white/10 bg-[#101b2e] px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500"
                    />
                    <UIButton variant="ghost" onClick={handleManualExecutionNote} disabled={executionSaving}>
                      Simpan Note
                    </UIButton>
                  </div>
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Recent Event History</p>
                  <div className="max-h-[220px] space-y-1.5 overflow-auto pr-1">
                    {selectedEvents.length === 0 ? (
                      <p className="text-xs text-slate-400">Belum ada event untuk incident ini. Jalankan aksi operasional untuk membentuk histori.</p>
                    ) : selectedEvents.map((event) => (
                      <div key={event.id} className="rounded-md border border-white/10 bg-[#13213a] px-2.5 py-2">
                        <div className="mb-0.5 flex items-center justify-between gap-2">
                          <p className="text-xs font-semibold text-white">{event.title}</p>
                          <StatusBadge tone={event.tone || 'neutral'}>{event.source || 'System'}</StatusBadge>
                        </div>
                        <p className="text-[11px] text-slate-300">{event.detail}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </SectionContainer>
        </div>
      </PageContainer>
      <CustomerImpactDrawer
        open={showImpactDrawer}
        onClose={() => setShowImpactDrawer(false)}
        title="Semua Customer Terdampak"
        subtitle={selectedIncident ? `Context incident ${selectedIncident.id}` : ''}
        customers={customerImpactSummary.customers || []}
        recoveryIncidentId={selectedIncident?.id || ''}
      />
    </AppShell>
  );
}







